<!-- 
    Can be used simply by passing in physician data with options for sorting
    example:
        <physician-map-and-table
            physician_data="{{ $physicians }}" 
            default_sort_field="last_name"
            default_sort_direction="asc"
            default_per_page="5"
            default_latitude="{{ Auth::user()->client->latitude }}"
            default_longitude="{{ Auth::user()->client->longitude }}"
            add_fields='[{ "name": "referral_count", "title": "Referalls", "visible": true, "width": "50px","after_name":"last_checkin"}]'
            remove_field_names=''
            :client_metrics="{{ Auth::user()->client->client_metrics }}"
        />
    can also replace physician_data with api_url - although not actively supported/tested. 
-->
<template>
<div class="table-map-view" :class="config.layout">

	<!--   ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~   START TOOLBAR   ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~   -->
    <div class="d-flex justify-content-between align-items-center">
    
        <!-- Filters -->
        <div class="d-flex form-row">                
            <!-- To Add Different Filters - simply place html inside the physician-map-and-table-api-mode wrapper and it will show up inside the 'slot' section here - with 2 way binding -->                
            <slot>
				<button 
					v-if="advancedFiltersAvailable"
					type="button" 
					class="btn btn-sm mr-2 mb-2"
					style="margin-left:5px" 
					:class="filtersInPlace ? 'btn-secondary' : 'btn-light'"
					aria-haspopup="true" aria-expanded="false"
					@click="toggleExpandAdvancedFilters()"
				>
					<i class="fas fa-filter text-xs" :class="filtersInPlace ? 'text-white' : 'text-gray-600'"></i> 
					<span class="hidden_mobile pr-1">Filters</span>
					<i :class="expandAdvancedFilters ? 'far fa-angle-left' : 'far fa-angle-right'" />                        
				</button>

				<filter-bar
					:filterTextName="moreParams['filter[search_full_name]']"
					placeholder="Search Name"
					@filter-set="setFilter($event)"
					@filter-reset="resetAllFilters()"
				/>

				<a v-if="filtersInPlace" class="btn btn-sm btn-ghost" @click="resetAllFilters()">
					Reset <span class="hidden_mobile">All Filters</span>
				</a>
            </slot>
        </div>
        
        <!-- Map Button -->
		<div :class="{ 'btn-group' : config.layout == 'desktop' }">
		    <button
		    	v-if="config.layout == 'desktop'"
		        class="btn btn-sm mb-2"
		        @click="toggleExpandMap()"
		        :class="mapExpanded ? 'btn-secondary' : 'btn-light'"
		    >
		        <i :class="mapExpanded ? 'far fa-angle-right' : 'far fa-angle-left'"></i>
		        <i :class="mapExpanded ? 'text-white' : 'text-gray-600'" class="fas fa-map-marked-alt"></i>
		        <span v-html="mapExpanded ? 'Shrink map' : 'Expand map'" />
		    </button>

		    <button 
		        class="btn btn-sm mb-2"
		        @click="toggleExpandMapSetting()"
		        :class="mapSettingsExpanded ? 'btn-secondary' : 'btn-light '"
		    >
		        <i :class="mapSettingsExpanded ? 'far fa-angle-right' : 'far fa-angle-left'"></i>
		        <i :class="mapSettingsExpanded ? 'text-white' : 'text-gray-600'" class="fas fa-cog"></i>
		    </button>
		</div>
    </div>
	<!--   ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~    END TOOLBAR    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~   -->

    <div class="fill-app-height card flex-row mb-0">
        <img v-if="!data_loaded" class="center-me" src="/images/loading-color.svg">

		<!--   ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~   START FILTERS   ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~   -->
        <div v-if="expandAdvancedFilters" class="filters h-100 bg-white" :class="view">
			<div class="card-header pb-2">
				<i class="fas fa-shapes text-gray-400"></i> Provider Type
			</div>
			<div class="px-35 pt-2 pb-25 text-sm">
				<div class="custom-control custom-radio custom-control-inline w-100">
					<input 
						type="radio" 
						id="providerAll" 
						name="providerType" 
						class="custom-control-input"
						:checked="providerTypeFilter == 'all'"
						@click="setFilterProviderType('all')"
					>				
					<label class="custom-control-label pt-1" for="providerAll">All</label>
				</div>
				<div class="custom-control custom-radio custom-control-inline w-100">
					<input 
						type="radio" 
						id="providerPhysician" 
						name="providerType" 
						class="custom-control-input"
						:checked="providerTypeFilter == 'physician'"
						@click="setFilterProviderType('physician')"
					>
					<label class="custom-control-label pt-1" for="providerPhysician">Only Physicians</label>
				</div>
				<div class="custom-control custom-radio custom-control-inline w-100">
					<input 
						type="radio" 
						id="providerGroup" 
						name="providerType" 
						class="custom-control-input"
						:checked="providerTypeFilter == 'group'"
						@click="setFilterProviderType('group')"
					>
					<label class="custom-control-label pt-1" for="providerGroup">Only Groups</label>
				</div>
			</div>

			<div class="card-header pb-2">
				<i class="fas fa-map-marker-alt text-gray-400"></i> Locations
			</div>
			<div class="px-35 pt-2 pb-25 text-sm">
				<div class="custom-control custom-radio custom-control-inline">
					<input 
						type="radio" 
						id="hasAddress2" 
						name="hasAddress1" 
						class="custom-control-input"
						:checked="!addressFilter"
						@click="filterAddresses(false)"
					>
					<label class="custom-control-label pt-1" for="hasAddress2">All Providers</label>
				</div>
				<div class="custom-control custom-radio custom-control-inline">
					<input 
						type="radio" 
						id="hasAddress1" 
						name="hasAddress1" 
						class="custom-control-input"
						:checked="addressFilter"
						@click="filterAddresses(true)"
					>
					<label class="custom-control-label pt-1" for="hasAddress1">Only w/ Locations</label>
				</div>				
			</div>
            
			<div class="card-header pb-2">
				<i class="fas fa-stethoscope text-gray-400"></i> Specialties
				<i v-if="specialtiesChanged"
					@click="specialtyFilter = []"
					class="far fa-times-circle text-gray-400 ml-1" />
			</div>                    
			<div class="mt-25 mb-25 px-35 text-sm">
				<div class="custom-control custom-checkbox" v-for="specialty in specialties">
					<input type="checkbox" 
						:id="specialty.label"
						v-bind:value="specialty.id"
						v-model="specialtyFilter"
						class="custom-control-input"
					>
					<label class="custom-control-label pt-1" :for="specialty.label" v-html="specialty.label" />
				</div>
			</div>

			<div class="card-header pb-2">
				<i class="fas fa-chart-bar text-gray-400"></i> Metrics
			</div>

			<div class="text-sm px-35">
				<div class="d-flex mt-3 mb-4 align-items-baseline">
					<i class="far fa-calendar-check mr-1 text-secondary"></i> Days since Last Checkin
					<i v-if="lastCheckinChanged" @click="resetSliderToDefault('last_checkin')" class="far fa-times-circle text-gray-600 ml-1" />
				</div>
				<vue-slider
					ref="lastCheckinSlider"
					v-bind="sliderOptions.last_checkin"
					v-model="sliderRangeData.last_checkin"
					tooltip="always"
				/>

				<div class="d-flex mt-25 pt-25 mb-4 align-items-baseline border-top">
					<i class="far fa-star-half-alt mr-1 text-secondary"></i> Referrer Score
					<i v-if="referrerScoreChanged" @click="resetSliderToDefault('referrer_score')" class="far fa-times-circle text-gray-600 ml-1" />
				</div>
				<vue-slider
					ref="referrerScoreSlider"
					v-bind="sliderOptions.referrer_score"
					v-model="sliderRangeData.referrer_score"
					tooltip="always"
				/>

				<div class="d-flex mt-25 pt-25 mb-4 align-items-baseline border-top">
					<i class="far fa-user-injured mr-1 text-secondary"></i> Referred Patients
					<i v-if="referredPatientsChanged" @click="resetSliderToDefault('referred_patients')" class="far fa-times-circle text-gray-600 ml-1" />
				</div>
				<vue-slider
					ref="referredPatientsSlider"
					v-bind="sliderOptions.referred_patients"
					v-model="sliderRangeData.referred_patients"
					tooltip="always"
				/>

				<div class="d-flex mt-25 pt-25 mb-4 align-items-baseline border-top">
					<i class="far fa-hospital-user mr-1 text-secondary"></i> Patient Visits
					<i v-if="patientVisitsChanged" @click="resetSliderToDefault('patient_visits')" class="far fa-times-circle text-gray-600 ml-1" />
				</div>
				<vue-slider
					ref="patientVisitsSlider"
					v-bind="sliderOptions.patient_visits"
					v-model="sliderRangeData.patient_visits"
					tooltip="always"
				/>

				<div class="d-flex mt-25 pt-25 mb-4 align-items-baseline border-top">
					<i class="far fa-money-check-edit-alt mr-1 text-secondary"></i> Generated Charges
					<i v-if="generatedChargesChanged" @click="resetSliderToDefault('generated_charges')" class="far fa-times-circle text-gray-600 ml-1" />
				</div>
				<vue-slider
					ref="generatedChargesSlider"
					v-bind="sliderOptions.generated_charges"
					v-model="sliderRangeData.generated_charges"
					tooltip="always"
				/>

				<div class="d-flex mt-25 pt-25 mb-4 border-top">
					<i class="far fa-route mr-1 text-secondary"></i> Distance from {{ physician_distance_use_clinic ? 'Clinic' : 'Me' }}
					<i v-if="distanceChanged" @click="resetSliderToDefault('distance')" class="far fa-times-circle text-gray-600 ml-1" />
				</div>
				<vue-slider
					class="mb-3"
					ref="distanceSlider"
					v-bind="sliderOptions.distance"
					v-model="sliderRangeData.distance"
					tooltip="always"
				/>
            </div>
        </div> 
		<!--   ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~    END FILTERS    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~   -->

        <div class="card-table d-flex h-100" :class="view">
            <div class="physicians-table vuetable-wrapper w-100 h-100" :class="{'showing_alternative_locations': !showPrimaryLocationsOnly}">
                <vuetable                
                	ref="vuetable"
                    :api-url="api_url ? api_url : null"
                    :api-mode="api_url ? true : false"                	
                    :fields="physicianFields"
                    :css="css.table"
                    table-height="35"
                    :multi-sort="false"
                    :append-params="moreParams"
                    :per-page="per_page"
                    :sort-order="sortOrder"
                    :sort-params="getSortParam"
					:options="options"
					:no-data-template="emptyText"
                    @vuetable:load-success="onDataLoaded"
                    @vuetable:pagination-data="onPaginationData"
                    @vuetable:row-clicked="onRowClicked"
                    :data-manager="dataManager"
                    pagination-path="pagination"
                >

                    <template slot="last_name" slot-scope="props">
                        <div class="d-flex align-items-center">
							<input
							 	v-if="showPrimaryLocationsOnly"
								class="mr-1" type="checkbox"
								v-model="selectedItems"
								:value="{
									display_name: props.rowData.display_name,
									location_id: props.rowData.location_primary ? props.rowData.location_primary.id : null,
									morphable_id: props.rowData.id,
									morphable_type: props.rowData.model,
									short_address: props.rowData.location_primary ? props.rowData.location_primary.short_address : null,
								}"
								@click="locationIsMappable(props.rowData.location_primary) ? sendSelectionToMap(props.rowData.location_primary.id) : null"
								data-test="providerPrimaryLocation"
					        />

							<div class="rounded-circle circle-cropped avatar-sm mr-2 text-center font-weight-bold text-sm">
								<div v-if="vizOption" 
									class="text-white"
									:style="'background-color:' + props.rowData.iconFillColor"
								>
									<span v-if="vizOption == 'vizLastCheckin'" v-html="props.rowData.last_checkin ? props.rowData.last_checkin.days_since : 'na'" />
									<span v-if="vizOption == 'vizReferrerScore'" v-html="props.rowData.referrer_score ? props.rowData.referrer_score : 0" />
									<span v-if="vizOption == 'vizReferredPatients'" v-html="props.rowData.referred_patients ? props.rowData.referred_patients : 0" />
									<span v-if="vizOption == 'vizPatientVisits'" v-html="props.rowData.patient_visits ? props.rowData.patient_visits : 0" />
									<span v-if="vizOption == 'vizGeneratedCharges'" v-html="props.rowData.generated_charges ? numeral(props.rowData.generated_charges).format('0a') : 0" />
									<span v-if="vizOption == 'vizDistance'">
										{{ props.rowData.distance ? Math.floor(props.rowData.distance) : 0 }}m
									</span>
								</div>
								<template v-else>
									<img v-if="props.rowData.photo_path" :src="props.rowData.photo_url" class="img-fluid">
									<div v-else class="bg-gray-200 text-gray-600 border">{{ props.rowData.initials }}</div>
								</template>
							</div>

							<div class="flex-grow-1">
					        	<div class="d-flex text-capitalize">
					        		<span class="text-nowrap truncated_name">
						        		{{ props.rowData.display_name }}
					        		</span>

					        		<i v-if="props.rowData.system_links && props.rowData.system_links.length > 0 && 
					        			props.rowData.system_links[0].system_id != null"
					        			class="far fa-link text-sm text-gray-500 ml-1"
					        			v-tooltip.bottom="'linked to EMR'" />

					        		<i v-if="!locationIsMappable(props.rowData.location_primary)"
					        			class="far fa-map-marker-alt-slash text-sm text-gray-500 ml-1"
					        			v-tooltip.bottom="'No location'"
					        			data-test="nonMappablePhysician" />

									<div v-if="!save_physicians_first" class="ml-2 drop-down dropleft" style="margin-top:-.25rem;">
										<a href="#" role="button" id="dropdownMenuLink" data-toggle="dropdown">
										    <i class="far fa-ellipsis-h text-xl"></i>
										</a>
										<div class="dropdown-menu py-0">
											<router-link :to="{ name: props.rowData.model == 'App\\Group' ? 'groups.show' : 'physicians.show', params:{ id:props.rowData.id } }" class="dropdown-item" data-test="providerProfileLink">
												View Profile
											</router-link>
										    <span class="dropdown-item" @click="buildCheckinModal(props.rowData.id, props.rowData.model, props.rowData.display_name)" data-toggle="modal" data-target="#myModal">Add Check-in</span>
										</div>
									</div>							        			
					        	</div>

								<span
									v-for="specialty in props.rowData.specialties"
									class="badge bg-teal-200 text-teal-700 mr-1"
									v-html="specialty.label"
								/>
								<!-- START RADAR -->
								<span
									v-if="props.rowData.specialty"
									class="badge bg-teal-200 text-teal-700 mr-1"
									v-html="props.rowData.specialty"
								/>
								<!--  END RADAR  -->							
					    	</div>
                        </div>

                        <template v-if="!showPrimaryLocationsOnly">
                        	<div 
                        		v-if="!locationIsMappable(props.rowData.location_primary)"
                        		class="d-flex pl-35 ml-25 mt-2 leading-base align-items-top text-sm text-gray-700">

								<input
									class="mr-1" type="checkbox"
									v-model="selectedItems"
									:value="{
										display_name: props.rowData.display_name,
										location_id: null,
										morphable_id: props.rowData.id,
										morphable_type: props.rowData.model,
										short_address: null,
									}"
									title="No Location Found"
						        />						        
						        <span class="text-gray-500">
						        	No location; saves to campaign only
						        </span>
						    </div>

							<div
								v-if="location.short_address != ''"
								v-for="location in props.rowData.locations"
								class="d-flex pl-35 ml-25 mt-2 leading-base align-items-top text-sm text-gray-700">
								<input
									class="mr-1" type="checkbox"
									v-model="selectedItems"
									:value="{
										display_name: props.rowData.display_name,
										location_id: location.id,
										morphable_id: props.rowData.id,
										morphable_type: props.rowData.model,
										short_address: location.short_address,
									}"
									@click="sendSelectionToMap(location.id)"
						        />
								<span 
									:class="{'text-gray-600': !location.show_in_maps}"
									@click="showLocationInMap(location)"
									v-html="location.short_address"
								/>
							</div>
                        </template>
                    </template>

			        <template slot="last_checkin_id" slot-scope="props" v-if="props.rowData.last_checkin_id">
			        	<span class="badge" :class="props.rowData.last_checkin.checkin_type.css_class_name">{{ props.rowData.last_checkin.checkin_type.label }}</span>
			        	<div class="mt-1 text-sm text-gray-600 leading-none">{{ moment(props.rowData.last_checkin.created_at).format('MMM D, YYYY') }}</div>          
			        </template>                    
                </vuetable>
                
                <div class="vuetable_fixed_footer d-flex" :class="footerDisplayType">
                    
                    <div class="d-flex select_all_checkbox_wrapper">
                        <input
                            class="select_all_checkbox"
                            type="checkbox"
                            title="Select all items"
                            :disabled="total_items == 0"
                            :checked="selectedAllVisibleLocations"
                            :indeterminate.prop="selectedSomeVisibleLocations"
                            @click="toggleAllVisibleItemsForSelection()"
                            data-test="selectAllPhysicians"
                        />
                    </div>

                    <div v-if="showPagination" class="background-fix vuetable-pagination d-flex mb-3 mr-2">
                        <vuetable-pagination-info
                        	class="flex-fill"
                            ref="paginationInfo"
                            info-class="pagination-info" 
                            :info-template="footerDisplayType == 'lg' ? '{from} to {to} of {total}' : '{from}-{to} of {total}'" />

                        <vuetable-pagination-bootstrap
                        	class="flex-fill text-right"
                        	ref="pagination"
                        	:on-each-side="1"
                        	:css="css.pagination"
                        	@vuetable-pagination:change-page="onChangePage" />
                    </div>

                    <div v-else class="background-fix vuetable-pagination d-flex mb-3">
                        <span class="vuetable-pagination-info">
                            {{ this.total_items }} results
                        </span>
                    </div>

					<div class="dropup flex-grow-1">
	                    <button
	                    	type="button"
	                        class="btn btn-block btn-lg leading-none dropdown-toggle"
	                        :class="selectedItems.length < 1 ? 'btn-light' : 'btn-primary'"
	                        :disabled="selectedItems.length < 1"
	                        data-toggle="dropdown"
	                        data-test="addToSelectorButton">
	                    	Add to...
	                    </button>
						<div class="dropdown-menu dropdown-menu-right py-0 w-100">
							<span class="dropdown-item" @click="setRouteVisits()" data-toggle="modal" data-target="#routeCreateModal" data-test="addToRouteButton">
								<i class="fal fa-route mr-1"></i> New Route <span class="badge badge-primary">{{ routeItems.length }}</span>
							</span>
							<span class="dropdown-item" @click="setCampaignVisits()" data-toggle="modal" data-target="#campaignCreateModal" data-test="addToCampaignButton">
								<i class="fal fa-fire-alt mr-1"></i> New Campaign <span class="badge badge-primary">{{ campaignItems.length }}</span>
							</span>
						</div>
					</div>
                </div>
            </div>
        </div>

        <div v-if="data_loaded" class="card-map flex-grow-1" :class="view">
            <physician-map-simple
            	:data_loaded="data_loaded"
                :locations="physicianLocationsForMap"
                :selectedLocations="selectedLocations"
                :default_latitude="default_latitude"
                :default_longitude="default_longitude"
                :searchingWithinMap="searchInMapWithinRadius"
                :visualization_is_on="visualization_is_on"
            />
        </div>

        <!--   ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~   START SETTINGS   ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~   -->
        <div v-if="mapSettingsExpanded" class="settings h-100 text-sm" style="width: 200px; overflow:auto; border-left:1px solid #c9c9c9;">
            <div class="card-header pb-2">
            	<i class="fas fa-cog"></i> Map Settings
            </div>

            <div class="px-35 pt-2 pb-25 border-bottom" v-if="vizualationsAvailable">
                <div class="text-blue-700 font-weight-bold text-base">Visualize Map By</div>
                <div class="custom-control custom-radio custom-control-inline">
                    <input 
                        type="radio" 
                        id="vizOptionNone" 
                        name="vizOptionNone" 
                        class="custom-control-input"
                        :checked="vizOption == false"
                        @click="changeVizOption(false)"
                    >
                    <label class="custom-control-label pt-1" for="vizOptionNone">None</label>
                </div>

                <div class="custom-control custom-radio custom-control-inline">
                    <input 
                        type="radio" 
                        id="vizLastCheckin" 
                        name="vizLastCheckin" 
                        class="custom-control-input"
                        :checked="vizOption == 'vizLastCheckin'"
                        @click="changeVizOption('vizLastCheckin')"
                    >
                    <label class="custom-control-label pt-1" for="vizLastCheckin">Since last check-in</label>
                </div>

                <div class="custom-control custom-radio custom-control-inline">
                    <input 
                        type="radio" 
                        id="vizReferrerScore" 
                        name="vizReferrerScore" 
                        class="custom-control-input"
                        :checked="vizOption == 'vizReferrerScore'"
                        @click="changeVizOption('vizReferrerScore')"
                    >
                    <label class="custom-control-label pt-1" for="vizReferrerScore">Referrer Score</label>
                </div>

                <div class="custom-control custom-radio custom-control-inline">
                    <input 
                        type="radio" 
                        id="vizReferredPatients" 
                        name="vizReferredPatients" 
                        class="custom-control-input"
                        :checked="vizOption == 'vizReferredPatients'"
                        @click="changeVizOption('vizReferredPatients')"
                    >
                    <label class="custom-control-label pt-1" for="vizReferredPatients">Referred patients</label>
                </div>

                <div class="custom-control custom-radio custom-control-inline">
                    <input 
                        type="radio" 
                        id="vizPatientVisits" 
                        name="vizPatientVisits" 
                        class="custom-control-input"
                        :checked="vizOption == 'vizPatientVisits'"
                        @click="changeVizOption('vizPatientVisits')"
                    >
                    <label class="custom-control-label pt-1" for="vizPatientVisits">Patient visits</label>
                </div>

                <div class="custom-control custom-radio custom-control-inline">
                    <input 
                        type="radio" 
                        id="vizGeneratedCharges" 
                        name="vizGeneratedCharges" 
                        class="custom-control-input"
                        :checked="vizOption == 'vizGeneratedCharges'"
                        @click="changeVizOption('vizGeneratedCharges')"
                    >
                    <label class="custom-control-label pt-1" for="vizGeneratedCharges">Charges generated</label>
                </div>

                <div class="custom-control custom-radio custom-control-inline">
                    <input 
                        type="radio" 
                        id="vizDistance" 
                        name="vizDistance" 
                        class="custom-control-input"
                        :checked="vizOption == 'vizDistance'"
                        @click="changeVizOption('vizDistance')"
                    >
                    <label class="custom-control-label pt-1" for="vizDistance">Distance</label>
                </div>

				<div v-if="vizOption" class="d-flex mt-2 text-xs text-white font-weight-bold " >
					<div class="flex-fill w-20 pl-1" :style="'background:' + vizColors.worst">0</div>
					<div class="flex-fill w-20" :style="'background:' + vizColors.bad"></div>
					<div class="flex-fill w-20" :style="'background:' + vizColors.mid"></div>
					<div class="flex-fill w-20" :style="'background:' + vizColors.better"></div>

					<div class="flex-fill w-20 text-right pr-1" :style="'background:' + vizColors.best">
						<span v-if="vizOption == 'vizLastCheckin'" v-html="vizMax.last_checkin" />
						<span v-if="vizOption == 'vizReferrerScore'" v-html="vizMax.referrer_score" />
						<span v-if="vizOption == 'vizReferredPatients'" v-html="vizMax.referred_patients" />
						<span v-if="vizOption == 'vizPatientVisits'" v-html="vizMax.patient_visits" />
						<span v-if="vizOption == 'vizGeneratedCharges'" v-html="Math.floor(vizMax.generated_charges / 1000) + 'k'" />
						<span v-if="vizOption == 'vizDistance'" v-html="vizMax.distance" />
					</div>
				</div>
            </div>
            
            <div class="px-35 py-25 border-bottom" v-if="!api_url">
                <div class="text-blue-700 font-weight-bold text-base">Locations to Show</div>
                <div class="custom-control custom-radio custom-control-inline">
                    <input 
                        type="radio" 
                        id="showLocations2" 
                        name="showLocations1" 
                        class="custom-control-input"
                        :checked="showPrimaryLocationsOnly"
                        @click="showPrimaryLocationsOnly = true"
                    >
                    <label class="custom-control-label pt-1" for="showLocations2">Primary Only ({{ totalPrimaryLocations }})</label>
                </div>

                <div class="custom-control custom-radio custom-control-inline">
                    <input 
                        type="radio" 
                        id="showLocations1" 
                        name="showLocations1" 
                        class="custom-control-input"
                        :checked="!showPrimaryLocationsOnly"
                        @click="showPrimaryLocationsOnly = false"
                    >
                    <label class="custom-control-label pt-1" for="showLocations1">All Locations ({{ totalLocations }})</label>
          		</div>

                <div class="custom-control custom-checkbox pt-2">
                    <input type="checkbox" 
                        id="route_only"
                        class="custom-control-input"
                        :disabled="(selectedLocations.length == 0)"
                        :class="{'disabled': (selectedLocations.length == 0)}"   
                        v-model="showRouteSelectionOnly"
                        @click="showRouteSelectionOnly = !showRouteSelectionOnly" 
                    >
                    <label class="custom-control-label pt-1" for="route_only">Route Stops Only <span v-if="selectedLocations.length > 0">({{ selectedLocations.length }})</span></label>
                </div>
            </div>
            
            <div class="px-35 py-25 border-bottom" v-if="!api_url && !hide_search_within_map">
                <div class="text-blue-700 font-weight-bold text-base">Search Within Map</div>

                <div class="custom-control custom-checkbox">
                    <input type="checkbox" 
                        id="search_within_map"
                        class="custom-control-input"
                        @click="toggleSearchWithinMap()" 
                        :checked="searchInMapWithinRadius"
                    >
                    <label class="custom-control-label pt-1" for="search_within_map">Radius Search</label>
                </div>
                
                <template v-if="searchInMapWithinRadius">
					<div v-if="searchInMapWithinRadiusIsReady" class="text-gray-500 mt-2 bg-green-100 text-green-800 p-2 rounded">
						{{ total_items }} physicians in {{ parseFloat(searchInMapWithinRadiusOptions.miles).toFixed(1) }} miles
					</div>
					<div v-else class="bg-yellow-100 text-yellow-800 mt-2 p-2 rounded">
						Click on the map, then adjust circle to see resulting physicians. 
					</div>
                </template>
            </div>
        </div>
		<!--   ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~    END SETTINGS    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~   -->            
    </div>


	<section id="bottom-menu" class="d-md-none navbar fixed-bottom bg-white text-center text-sm border-top mx-auto p-0" style="z-index:1010">
        <div class="col btn btn-ghost btn-sm text-gray-700 text-sm py-1" @click="setView('view-list')" :class="{ 'bg-gray-100' : view == 'view-list' }">
            <i class="text-secondary pt-1 fal fa-user"></i><br>
            View List
        </div>
        <div class="col btn btn-ghost btn-sm text-gray-700 text-sm py-1" @click="setView('view-map')" :class="{ 'bg-gray-100' : view == 'view-map' }">
            <i class="text-secondary pt-1 fal fa-map-marker-alt"></i><br>
            View Map
        </div>
        <div v-if="selectedItems.length < 1" class="col btn btn-ghost btn-sm disabled text-gray-500 text-sm py-1">
            <i class="text-gray-500 pt-1 fal fa-plus-circle"></i><br>
            Add to...
        </div>

        <div v-else class="dropup col btn btn-ghost btn-sm text-gray-700 text-sm py-1">
			<div class="dropdown-menu dropdown-menu-right py-0 mr-2">
				<span class="dropdown-item" @click="setRouteVisits()" data-toggle="modal" data-target="#routeCreateModal">
					<i class="fal fa-route mr-1"></i> 
					Route <span class="badge badge-sm badge-primary" style="top:auto">{{ routeItems.length }}</span>
				</span>
				<span class="dropdown-item" @click="setCampaignVisits()" data-toggle="modal" data-target="#campaignCreateModal">
					<i class="fal fa-fire-alt mr-1"></i> 
					Campaign <span class="badge badge-sm badge-primary" style="top:auto">{{ campaignItems.length }}</span>
				</span>
			</div>
			<div data-toggle="dropdown">
	            <i class="text-secondary pt-1 fal fa-plus-circle"></i><br>
	            Add to...
	        </div>
        </div>                   
    </section>

    <campaign-create-modal />
</div>
</template>

<script>

	import { mapGetters, mapState } from 'vuex';
	import Vuetable from 'vuetable-2';
	import VuetablePaginationInfo from 'vuetable-2/src/components/VuetablePaginationInfo';
	import VuetablePaginationBootstrap from './VuetablePaginationBootstrap.vue';
	import vueSlider from 'vue-slider-component';
	import FilterBar from './FilterBar';
	import CampaignCreateModal from './CampaignCreateModal';

  export default {     

    components: {
      CampaignCreateModal,
      'popper': Popper,
      Vuetable,
      VuetablePaginationInfo,
      VuetablePaginationBootstrap,
      vueSlider,
      FilterBar,
    },

    props: [ 
        'api_url' , 
        'default_latitude' , 
        'default_longitude', 
        'default_sort_field', 
        'default_sort_direction', 
        'default_per_page', 
        'data_loaded', 
        'physician_data', 
        'physician_fields_override',
        'remove_field_names', 
        'add_fields',
        'client_metrics',
        'save_physicians_first',
        'hide_search_within_map_prop'
    ],

    data() {
      return {
        physicians_data_data_mode_displayed_physicians: [],

        mapExpanded: false,
        mapSettingsExpanded: false,
        searchInMapWithinRadius: false,
        searchInMapWithinRadiusOptions: {
            latitude: null,
            longitude: null, 
            miles: null
        },
        showPrimaryLocationsOnly: true,
        totalPrimaryLocations: 0,
        totalLocations: 0,

        showRouteSelectionOnly: false,

        advancedFiltersAvailable: false, // will add only if client_metrics is available
        expandAdvancedFilters: false, 
        addressFilter: false,
        providerTypeFilter: 'all',

        max_distance_var: 10,
        max_distance_set: false,

        sliderRangeData: {}, //bound to sliders: does the filtering
        sliderRangeDataDefault: {}, // set on setVizParams() function

        specialtyFilter: [],

        vizualationsAvailable: false, // will add only if client_metrics is available
        vizOption: false,
        vizMax: {},

        segmentColors: {
            'empty': '#999999',
            0: '#5D505B',
            1: '#814554',
            2: '#A53B4E',
            3: '#C93148',
            4: '#EE2742',
        },

        resetTakingPlace: false,

        physicianFields: [           
            {
                name: 'last_name',
                title: 'Name',
                sortField: 'last_name',
                visible: true,
                width: '242px',
                dataClass: 'physician_info',
                visibility: {
                	def: true,
                	lim: true,
                	min: true,
                	mob: true,
                },
            },
            { 
                name: 'last_checkin_id',
                title: 'Last <i class="far fa-check-circle" title="Last Check-in"></i>',
                sortField: 'last_checkin_id',  
                visible: true,
                width: '100px',
                visibility: {
                	def: true,
                	lim: true,
                	min: false,
                	mob: false,
                },
            },
            {
                name: 'referrer_score',
                title: '<i class="far fa-star-half-alt" title="Referrer Score"></i>',
                sortField: 'referrer_score',
                visible: true,
                width: '58px',
                visibility: {
                	def: true,
                	lim: true,
                	min: false,
                	mob: false,
                },
            },
            {
                name: 'referred_patients',
                title: '<i class="far fa-user-injured" title="Referred Patients"></i>',
                sortField: 'referred_patients',
                visible: true,
                width: '58px',
                visibility: {
                	def: true,
                	lim: false,
                	min: false,
                	mob: false,
                },
            },
            {
                name: 'patient_visits',
                title: '<i class="far fa-hospital-user" title="Patient Visits"></i>',
                sortField: 'patient_visits',
                visible: true,
                width: '58px',
                visibility: {
                	def: true,
                	lim: false,
                	min: false,
                	mob: false,
                },
            },
            {
                name: 'generated_charges',
                title: '<i class="far fa-money-check-edit-alt" title="Generated Charges"></i>',
                sortField: 'generated_charges',
                formatter (value) {
                	if (value) {
                		return '$' + numeral(value).format('0a')
                	}
                },
                visible: true,
                width: '58px',
                visibility: {
                	def: true,
                	lim: false,
                	min: false,
                	mob: false,
                },
            },
            { 
                name: 'distance',
                title: '<i class="far fa-route" title="Distance"></i>',
                sortField: 'distance',
                formatter (value) {
                	if (value != null) {
                		return value + '<span class="text-sm">mi</span>';
                	}
                },
                visible: true,
                width: '60px',
                visibility: {
                	def: false,
                	lim: false,
                	min: false,
                	mob: true,
                },
            },
        ],

        //to know which display mode to use when things change
        displayFieldsTableExpanded: null,
        displayFieldsTableCollapsed: null,

        footerDisplayType: null,

        sortOrder: [{
            field: this.default_sort_field,
            direction: this.default_sort_direction,
        }],

        css: {
          table: {
            tableClass: 'table leading-tight',
            ascendingIcon: 'fas fa-sort-down',
            descendingIcon: 'fas fa-sort-up'
          },
          //pagination classes are in VuetabluePaginationBootstrap.vue
        },

        options: {
            sortIcon: {
                base : 'fa',
                is: 'fa-sort',
                up: 'fa-sort-asc',
                down: 'fa-sort-desc'
            }
        },

        per_page: (!isNaN(this.default_per_page)) ? parseInt(this.default_per_page) : 100,
        total_items: 0,

        moreParamsDefault: {
            'filter[search_full_name]': null,
            'filter[specialties]': [],
            'filter[addresses_only]': false,
            'filter[last_checkin]': [],
            'filter[referrer_score]': [],
            'filter[referred_patients]': [],
            'filter[patient_visits]': [],
            'filter[generated_charges]': [],
            'filter[distance]': [],
        },
        moreParams: {},

        checkin_options: {
        	physician: null,
        },

		selectedItems: [], //new combo items
		physiciansToSaveFirst: [], //only for RADAR

        physician_distance_use_clinic: true,
        physician_distance_from_latitude: this.default_latitude,
        physician_distance_from_longitude: this.default_longitude,
        user_latitude: null,
        user_longitude: null,

        // save_physicians_first: false,
        hide_search_within_map: false,

        view: 'view-list',
        emptyText: 'Loading...please wait',

      };
    },

    created() {
        this.getUsersLocation();

        if (typeof this.physician_fields_override !== "undefined") {
            this.physicianFields = this.physician_fields_override;
        }

        // client info present, set and show filters
        if (this.client_metrics) {
            this.setVizParams();
            this.advancedFiltersAvailable = true;
            this.vizualationsAvailable = true;
        }

        if (this.hide_search_within_map_prop === 'true' || this.hide_search_within_map_prop === true) {
            this.hide_search_within_map = true;
        }

        // params
        this.moreParams = {...this.moreParamsDefault};
    },

    activated() {
    	//selections
		this.selectedItems = [];
    },

    mounted() {
        this.$root.$on('select-location-from-map', (locationId) => { 
            //this.addSelectedLocation(locationId);
            let item = this.physicianLocationsForMap.find(mapItem => mapItem.id == locationId);
            this.selectedItems.push({
            	display_name: item.display_name,
				location_id: locationId,
				morphable_id: item.locationable_id,
				morphable_type: item.locationable_type,
				short_address: item.short_address ? item.short_address : null,
            });
        }); 

        this.$root.$on('deselect-location-from-map', (locationId) => { 
            //this.removeSelectedLocation(locationId);
            this.selectedItems.splice(this.selectedItems.findIndex(item => item.location_id == locationId) ,1);
        });

        this.$root.$on('search-within-map-radius-changed', (radiusOptions) => { 
            this.searchInMapWithinRadiusOptions = radiusOptions;
        }); 

        this.setLayout();
    },  

    methods: {
    	//---- GLOBAL AND APP-WIDE ----//
        buildCheckinModal(id, model, display_name) {
			this.checkin_options.checkinables = {
				morphable_id : id,
				morphable_type : model,
				display_name : display_name,
			};
			this.$root.$emit('build_checkin_modal', { checkin_options: this.checkin_options });
        },

        changeVizOption(newOption) {
            this.vizOption = newOption;
            Vue.nextTick( () => this.$refs.vuetable.refresh() );
        },

        getUsersLocation() {
            navigator.geolocation.getCurrentPosition(
                pos => { 
                    //got location, set for later
                    this.user_latitude = pos.coords.latitude;
                    this.user_longitude = pos.coords.longitude;
                    this.physician_distance_use_clinic = false;
                }, err => {
                    console.warn(`ERROR(${err.code}): ${err.message}`);
                }, {
                    enableHighAccuracy: true,
                },
            );
        },

        setCampaignVisits() {
        	this.$root.$emit('build_campaign_modal', { 
        		campaign_stops: this.save_physicians_first ? this.physiciansToSaveFirst : this.campaignItems,
        		save_physicians_first: this.save_physicians_first
        	});
        }, 

        setRouteVisits() {
        	this.$root.$emit('build_route_modal', { 
        		route_stops: this.save_physicians_first ? this.physiciansToSaveFirst : this.routeItems,
        		save_physicians_first: this.save_physicians_first
        	});
        }, 

        setVizParams() {
            if (!this.client_metrics) { 
            	return;
            }
            
            //set the maxes, just a helper for other displays
            this.vizMax = {
            	last_checkin: 180,
            	referrer_score: this.client_metrics.max_referrer_score,
            	referred_patients: this.client_metrics.max_referred_patients,
            	patient_visits: this.client_metrics.max_patient_visits,
            	generated_charges: this.client_metrics.max_generated_charges,
            	distance: Math.floor(this.max_distance_var) + 1,
            };

            //and the slider default values
            this.sliderRangeDataDefault = {
                last_checkin: [0, this.vizMax.last_checkin],
                referrer_score: [0, this.vizMax.referrer_score],
                referred_patients: [0, this.vizMax.referred_patients],
                patient_visits: [0, this.vizMax.patient_visits],
                generated_charges: [0, this.vizMax.generated_charges],
                distance: [0, this.vizMax.distance]
            };

            //and reset the active sliders
            this.sliderRangeData = {...this.sliderRangeDataDefault};
        },

        setMaxDistance() {
        	//distance is not in client metrics, has to set at runtime
        	if (!this.max_distance_set) {
	            this.vizMax.distance = Math.floor(this.max_distance_var) + 1;
	            this.sliderRangeDataDefault.distance = [0, this.vizMax.distance];
	            this.sliderRangeData.distance = [0, this.vizMax.distance];
	            this.max_distance_set = true;
	        }
        },


    	//---- LAYOUT SPECIFIC ----//
        setLayout() {
        	let footerDisplayType = 'sm';

        	if (this.$root.$mq == 'xs') {
        		this.mapExpanded = false;
        		this.displayFieldsTableExpanded = 'mob';
        		this.displayFieldsTableCollapsed = 'mob';
        		footerDisplayType = 'sm';
        	}

        	if (this.$root.$mq == 'sm') {
        		this.mapExpanded = false;
        		this.displayFieldsTableExpanded = 'def';
        		this.displayFieldsTableCollapsed = 'def';
        		footerDisplayType = 'md';
        	}

        	if (this.$root.$mq == 'md') {
        		this.displayFieldsTableExpanded = 'lim';
        		this.displayFieldsTableCollapsed = 'min';
        		footerDisplayType = 'sm';
        	}

        	if (this.$root.$mq == 'lg') {
        		this.displayFieldsTableExpanded = 'lim';
        		this.displayFieldsTableCollapsed = 'min';
        		footerDisplayType = 'sm';
        	}

        	if (this.$root.$mq == 'xl') {
        		this.displayFieldsTableExpanded = 'def';
        		this.displayFieldsTableCollapsed = 'lim';
        		footerDisplayType = 'lg';
        	}

        	if (this.mapExpanded) {
        		this.setTableFields(this.displayFieldsTableCollapsed);
        		this.footerDisplayType = 'xs';
        	} else {
        		this.setTableFields(this.displayFieldsTableExpanded);
        		this.footerDisplayType = footerDisplayType;
        	}

        	if (this.view == 'view-map' || this.config.layout == 'desktop') {
            	this.$root.$emit('map-adjust');      
            }    	
        },

        setView(value) {
        	//toggles map and list view on mobile
        	this.view = value;
        	if (this.view == 'view-map') {
	        	this.$root.$emit('map-adjust'); 
	        }
        },

        toggleExpandAdvancedFilters() {            
            this.expandAdvancedFilters = !this.expandAdvancedFilters;
        }, 

        toggleExpandMap() {
            this.mapExpanded = !this.mapExpanded;
        }, 

        toggleExpandMapSetting() {
            this.mapSettingsExpanded = !this.mapSettingsExpanded;
        },


        //---- VUETABLE SPECIFIC ----//
        setTableFields(value) {
        	//supported values are 'def', 'lim', 'min', 'mob'
        	this.physicianFields.forEach(field => {
        		field.visible = field.visibility[value];
        	});

        	this.$refs.vuetable.normalizeFields();
        },

        dataManager(sortOrder, pagination) {

        	// used in data mode only
            let local = this.physician_data_data_mode;
            // sortOrder can be empty, so we have to check for that as well
            if (sortOrder.length > 0) {
                let sortField = sortOrder[0].sortField;
                let sortDirection = sortOrder[0].direction;
                let isAscending = (sortDirection == "asc");
                let stringFields = ["last_name", "specialties", "location_primary", "phone", "last_checkin"];
                let integerFields = ["referrer_score", "referred_patients", "patient_visits", "generated_charges", "distance"];
                let fieldIsString = (stringFields.indexOf(sortField) !== -1);

                local = _.orderBy(
                    local,
                    [( o ) => { 
                        // lots of work to push the bad apples to the bottom
                        
                        // remove initial blank spaces from string
                        // if (fieldIsString) {
                        //     o[sortField] = o[sortField].replace(/\s+/g, '');
                        // }

                        // good values
                        if (o[sortField]) {
                            return o[sortField];
                        }

                        // for 'distance' return 0's as part of sort order, instead of counting as blanks. 
                        if (sortField == "distance" && o[sortField] == 0) {
                            return o[sortField]; 
                        }
                        // last names that don't exist
                        if (sortField == "last_name" && !o[sortField] && o["display_name"]) {
                            return o["display_name"]; 
                        }

                        // bad strings
                        if (fieldIsString) {
                            return '';
                        } 
                        // bad numbers
                        return isAscending ? Infinity : -Infinity;

                    }], 
                    sortDirection
                );
            }

            pagination = this.$refs.vuetable.makePagination(
                local.length,
                this.per_page
            );

            let from = pagination.from - 1;
            let to = from + this.per_page;

            this.total_items = local.length;

            let displayed_physicians = _.slice(local, from, to);

            this.physicians_data_data_mode_displayed_physicians = displayed_physicians;

            
            if (this.data_loaded && displayed_physicians.length == 0) {
            	this.emptyText = 'No records found';
            }

            return {
                pagination: pagination,
                data: displayed_physicians
            };

        },

        getSortParam: function(sortOrder) {
			return sortOrder.map(function(sort) {
				return (sort.direction === 'desc' ? '-' : '') + sort.field
			}).join(',')
        },

        onChangePage(page) {
			this.$refs.vuetable.changePage(page);
        },

        onDataLoaded(response) {
            this.physician_data = response.data.data;
            this.total_items = response.data.total;
            this.data_loaded = true;
			Vue.nextTick( () => {
				if (this.$refs.vuetable.tableData.length == 0 || this.physician_data.length == 0) {
					this.emptyText = 'No records found';
				}
			});       
        },

        onPaginationData(paginationData) {
            if (this.showPagination) {
                this.$refs.pagination.setPaginationData(paginationData);
                this.$refs.paginationInfo.setPaginationData(paginationData);
            }
        },

        onRowClicked(row) {
            if (this.showPrimaryLocationsOnly) {
                this.showLocationInMap(row.data.location_primary);
            }
        },

        showLocationInMap(location) {
            if (this.locationIsMappable(location)) {
                this.$root.$emit('show-location', location.id);
            }
        },

        toggleSearchWithinMap() {
            this.searchInMapWithinRadius = !this.searchInMapWithinRadius;
            if (!this.searchInMapWithinRadius && this.searchInMapWithinRadiusIsReady) {
	            this.searchInMapWithinRadiusOptions =  {
	                latitude: null,
	                longitude: null, 
	                miles: null
	            };
            }
        },

        toggleAllVisibleItemsForSelection() {
			// if nothing's selected add all
			if (this.selectedItems.length === 0) {                                
				this.addAllVisibleItems();
			}
			// some selected, remove what's there and re-add
			else if (this.selectedSomeVisibleLocations == true) {
				this.selectedItems = [];
				this.addAllVisibleItems();
			}
			// all selected, clear everything
			else {
				this.selectedItems = [];
			}
			this.$root.$emit('map-adjust');
        },

		addAllVisibleItems() {
			if (this.api_url) {
				var physicians = this.physician_data_data_mode;
			} else {
				var physicians = this.physicians_data_data_mode_displayed_physicians;
			}

			if (this.showPrimaryLocationsOnly) {
				physicians.forEach(physician => {
					this.selectedItems.push({
						display_name: physician.display_name,
						location_id: physician.location_primary ? physician.location_primary.id : null,
						morphable_id: physician.id,
						morphable_type: physician.model,
						short_address: physician.location_primary ? physician.location_primary.short_address : null,					
					});
				});
			} else {
				physicians.forEach(physician => {
					if (!this.locationIsMappable(physician.location_primary)) {
						this.selectedItems.push({
							display_name: physician.display_name,
							location_id: null,
							morphable_id: physician.id,
							morphable_type: physician.model,
							short_address: null,					
						});						
					} else {
						physician.locations.forEach(location => {
							this.selectedItems.push({
								display_name: physician.display_name,
								location_id: location.id,
								morphable_id: physician.id,
								morphable_type: physician.model,
								short_address: location.short_address,					
							});										
						});
					}
				});				
			}
		},

		sendSelectionToMap(locationId) {
			if (this.selectedItems.some(item => item.location_id == locationId)) {
				this.$root.$emit('deselect-location', locationId);
			} else {
				this.$root.$emit('select-location', locationId);
			}			
		},

        filterAddresses($doFilter) {            
            this.addressFilter = $doFilter;
            this.moreParams['filter[addresses_only]'] = this.addressFilter;
            Vue.nextTick( () => this.$refs.vuetable.refresh() );
        },

        setFilter(filterTextName) {
            this.moreParams['filter[search_full_name]'] = filterTextName;
            Vue.nextTick( () => this.$refs.vuetable.refresh() );
        },

        setFilterProviderType(value) {
        	this.providerTypeFilter = value;
        	Vue.nextTick( () => this.$refs.vuetable.refresh() );
        },

        resetAllFilters() {
            this.resetTakingPlace = true;
            
            this.moreParams = {...this.moreParamsDefault};
            this.sliderRangeData = {...this.sliderRangeDataDefault};
            this.addressFilter = false;
            this.specialtyFilter = [];
            this.setVizParams();
            Vue.nextTick( () => this.$refs.vuetable.refresh() );

            setTimeout(() => {
                this.resetTakingPlace = false;
            }, 1000);            
        },

        resetSliderToDefault(name) {
        	this.sliderRangeData[name] = [...this.sliderRangeDataDefault[name]];
            Vue.nextTick( () => this.$refs.vuetable.refresh() );
        },

        getLocationsFromPhysicians(physicians) {
            var locations = [];
            physicians.forEach(physician => {
                var location_primary = physician.location_primary;
                
                if (!this.locationIsMappable(location_primary)) {
                    return;
                }

                locations.push(location_primary);
                this.totalPrimaryLocations++;

                if (typeof physician.locations !== "undefined" )
                {
                    //this.nonPrimaryLocationsExist = true;
                    this.totalLocations += physician.locations.length;

                    if (!this.showPrimaryLocationsOnly) {
                       physician.locations.forEach(location => {
                           if (!location.primary) {
                               var non_primary_location = location;
                               if (!this.locationIsMappable(non_primary_location)) {
                                     return;
                               }
                               locations.push(non_primary_location);
                           }
                       }); 
                    }
                }
            });
            return locations;
        },

        locationIsMappable(location) {
            if (location && location.latitude && location.longitude ) {
                return true;
            }
            return false;
        }, 

        // from https://stackoverflow.com/a/26837460/6010500
        calcLocationDistanceFromPoint(lat1, lon1, lat2, lon2, unit) {
            var radlat1 = Math.PI * lat1/180;
            var radlat2 = Math.PI * lat2/180;
            var radlon1 = Math.PI * lon1/180;
            var radlon2 = Math.PI * lon2/180;
            var theta = lon1-lon2;
            var radtheta = Math.PI * theta/180;
            var dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
            dist = Math.acos(dist);
            dist = dist * 180/Math.PI;
            dist = dist * 60 * 1.1515;
            if (unit=="K") { dist = dist * 1.609344; }
            if (unit=="N") { dist = dist * 0.8684; }
            return dist;
        },

        locationsWithinMilesOfLocation(locations, options) {
            var locations_within_radius = [];
            for (var i = 0; i < locations.length; i++) {
                let distance = this.calcLocationDistanceFromPoint(
                    options.latitude,
                    options.longitude,
                    locations[i].latitude,
                    locations[i].longitude
                );
                if (distance <= options.miles) {
                    locations_within_radius.push(locations[i]);
                } 
            }
            return locations_within_radius;
        },

        physiciansWithinMilesOfLocation(physicians, options) {
            // get all locations
            let locations = this.getLocationsFromPhysicians(physicians);
            // filter locations
            let filtered_locations = this.locationsWithinMilesOfLocation(locations, options);
            
            // grab physician ids
            var physician_ids = [];
            for (var i = filtered_locations.length - 1; i >= 0; i--) {
                if (physician_ids.indexOf(filtered_locations[i].physician_id) === -1) {
                    physician_ids.push(filtered_locations[i].physician_id);
                }
            }

            // filter physicians
            physicians = [...physicians];
            var filtered_physicians = [];
            for (var i = physicians.length - 1; i >= 0; i--) {
                if (physician_ids.indexOf(physicians[i].id) !== -1) {
                    filtered_physicians.push(physicians[i]);
                }
            }
                         
            return filtered_physicians;
        },

        specialtyFilterIdSelected(specialtyId) {
            return (this.specialtyFilter.indexOf(specialtyId) > -1);
        },

    },

    computed: {
    	//...mapGetters(['mappableProviderIds']),
    	...mapState(['config']),

    	campaignItems() {
    		return _.uniqBy(this.selectedItems, (item) => { return item.morphable_type + '_' + item.morphable_id; } );
    	},

    	routeItems() {
    		return this.selectedItems.filter(item => item.location_id != null && item.short_address != '');
    	},

        showPagination() {
            return this.total_items > parseInt(this.per_page);
        },

        physician_data_data_mode() {
            var physicians = this.providerTypeFilter == 'all' ? this.physician_data : 
            	this.physician_data.filter(provider => this.providerTypeFilter == 'group' ? provider.model == 'App\\Group' : provider.model == 'App\\Physician');
            
            if (physicians.length == 0) { return []; }
            
            // add visualization colors
            if (this.client_metrics) {
	    	    var segmentsCount = this.segmentColors ? Object.keys(this.segmentColors).length - 1 : 5; //5, otherwise one less for empty state
	            var segmentColors = this.segmentColors;
	            var vizOption = this.vizOption;
	            var client_metrics = this.client_metrics;
	        }

            //add useful meta info to each record
            physicians.forEach(physician => {
            	// add distance for 'miles' field
                if (physician.location_primary && physician.location_primary.show_in_maps) {
                    let distance = parseFloat((this.calcLocationDistanceFromPoint(
                        this.physician_distance_from_latitude,
                        this.physician_distance_from_longitude,
                        physician.location_primary.latitude,
                        physician.location_primary.longitude
                    )).toFixed(1));

                    if (distance < 100) {
                        physician.distance = distance;
                        // set vizmax                      
                        if (distance > this.max_distance_var) {
                            this.max_distance_var = distance;
                        }
                    }
                } 
            
            	// add visualization colors
                if (this.client_metrics) {
                	var segment = 'empty';

                    switch(vizOption) {
                        default: // none
                            break;
                        case 'vizLastCheckin':
                            if (physician.last_checkin) {
                                segment = Math.floor((180 - physician.last_checkin.days_since) / (180/segmentsCount));
                                //lower is better -- reverse the scale
                                if (segment < 0) { 
                                    segment = 0;
                                };
                                segment == segmentsCount ? segment = segmentsCount-1 : segment = segment;
                            }   
                            break;
                        case 'vizReferrerScore':
                            segment = Math.floor(physician.referrer_score / (100/segmentsCount));
                            break;
                        case 'vizReferredPatients':
                            segment = Math.floor(physician.referred_patients / (client_metrics.max_referred_patients/segmentsCount));
                            break;
                        case 'vizPatientVisits':
                            segment = Math.floor(physician.patient_visits / (client_metrics.max_patient_visits/segmentsCount));
                            break;
                        case 'vizGeneratedCharges':
                            segment = Math.floor(physician.generated_charges / (client_metrics.max_generated_charges/segmentsCount));
                            break;
                        case 'vizDistance':
                            // since 80% of physicians are within 30 miles - lets focus on those. 
                            segment = Math.floor(physician.distance / 3);

                            //outliers
                            segment > segmentsCount ? segment = segmentsCount : segment = segment;

                            //lower is better -- reverse the scale
                            segment = (segment * -1) + segmentsCount;
                            break;

                    }
                    physician.iconFillColor = segmentColors[segment];
                }

                // augment location data (with visualize icon, display_name, physician_id, etc)
                if (physician.location_primary) {
                	physician.location_primary.first_name = physician.first_name;
                	physician.location_primary.last_name = physician.last_name;
                    physician.location_primary.display_name = physician.display_name;
                    physician.location_primary.physician_id = physician.id;
                    physician.location_primary.iconFillColor = physician.iconFillColor;
                }
                if (typeof physician.locations !== "undefined" ) {
                	physician.locations.forEach(location => {
                		location.first_name = physician.first_name;
                		location.last_name = physician.last_name;                   	
                    	location.display_name = physician.display_name;
                    	location.physician_id = physician.id;
                    	location.iconFillColor = physician.iconFillColor;
                	}); 
                }

            });

            this.setMaxDistance();

            if (!this.filtersInPlace && !this.searchInMapWithinRadius) {
                return physicians;
            }

            // if searching on map
            if (this.searchInMapWithinRadius) {
                if (this.searchInMapWithinRadiusIsReady) {
                    var options = {...this.searchInMapWithinRadiusOptions};
                    physicians = this.physiciansWithinMilesOfLocation(physicians, options);

                } else {
                    physicians = [];
                }
            }

            // text search 
	        if (this.moreParams['filter[search_full_name]'] !== null) {
	            physicians = physicians.filter(physician => 
	                physician.display_name.toLowerCase().includes(this.moreParams['filter[search_full_name]'].toLowerCase())
	            );    
	        }

            //specialties search
            if (this.specialtiesChanged) {
            	physicians = physicians.filter(physician => 
            		physician.specialties.some(specialty => 
            			this.moreParams['filter[specialties]'].includes(specialty.id))
            	);
            }

            // has addresses
            if (this.moreParams['filter[addresses_only]'] !== this.moreParamsDefault['filter[addresses_only]']) {
                if (this.moreParams['filter[addresses_only]']) {
                    physicians = physicians.filter(physician => 
                    	physician.location_primary && 
                    	this.locationIsMappable(physician.location_primary)
                    );  
                }
            }


            // 'filter[last_checkin]': [],
            if (this.lastCheckinChanged) {
                physicians = physicians.filter(physician => 
                	physician.last_checkin && 
                	physician.last_checkin.days_since >= this.moreParams['filter[last_checkin]'][0] && 
                	physician.last_checkin.days_since <= this.moreParams['filter[last_checkin]'][1]
                );  
            }

            // referrer score
            if (this.referrerScoreChanged) {
                physicians = physicians.filter(physician => 
                    physician.referrer_score >= this.moreParams['filter[referrer_score]'][0] && 
                    physician.referrer_score <= this.moreParams['filter[referrer_score]'][1]
                );  
            }

            // referred patients
            if (this.referredPatientsChanged) {
                physicians = physicians.filter(physician => 
                    physician.referred_patients >= this.moreParams['filter[referred_patients]'][0] && 
                    physician.referred_patients <= this.moreParams['filter[referred_patients]'][1]
                );  
            }

            // patient visits
            if (this.patientVisitsChanged) {
                physicians = physicians.filter(physician => 
                    physician.patient_visits >= this.moreParams['filter[patient_visits]'][0] && 
                    physician.patient_visits <= this.moreParams['filter[patient_visits]'][1]
                ); 
            }

            // generated charges
            if (this.generatedChargesChanged) {
                physicians = physicians.filter(physician => 
                    physician.generated_charges >= this.moreParams['filter[generated_charges]'][0] && 
                    physician.generated_charges <= this.moreParams['filter[generated_charges]'][1]
                );  
            }

            // generated charges
            if (this.distanceChanged) {
                physicians = physicians.filter(physician => 
                    physician.distance >= this.moreParams['filter[distance]'][0] && 
                    physician.distance <= this.moreParams['filter[distance]'][1]
                );
            }

            return physicians;
        },

        physicianLocations() {
            if (this.api_url) {
            	var physicians = this.physician_data_data_mode;
            } else {
                var physicians = this.physicians_data_data_mode_displayed_physicians;
            }

            if (!this.data_loaded || physicians.length === 0) { return []; }
            
            var locations = [];
            this.totalPrimaryLocations = 0;
            this.totalLocations = 0;

            locations = this.getLocationsFromPhysicians(physicians);

            return locations;
        }, 

        physicianLocationsForMap() {
            // if showing routes only... 
            if (this.showRouteSelectionOnly && this.selectedItems.length > 0) {
                return this.selectedLocations;
            } 
            return this.physicianLocations;
        }, 

        selectedLocations() {
        	return this.physicianLocations.filter(location => this.selectedItems.some(item => item.location_id == location.id));
        },

        searchInMapWithinRadiusIsReady() {
            return this.searchInMapWithinRadiusOptions.latitude && this.searchInMapWithinRadiusOptions.longitude && this.searchInMapWithinRadiusOptions.miles ? true : false;
        },  

        specialties() {
        	return this.$store.state.options.specialties;
        },

        sliderOptions() {
        	let options = { height: 4, dotSize: 12, interval: 1, lazy: true, min: 0 };
            return {                   
                last_checkin: { ...options, max: 180 },
                referrer_score: { ...options, max: 100 },
                referred_patients: { ...options, max: this.vizMax.referred_patients },
                patient_visits: { ...options, max: this.vizMax.patient_visits },
                generated_charges: { ...options, max: this.vizMax.generated_charges, tooltipFormatter: (v) => `${(Math.round(v/1000) + 'k')}`, },
                distance: { ...options, max: this.vizMax.distance },
            };
        },

        vizColors() {
            let vizColors = {
                worst: this.segmentColors[0],
                bad: this.segmentColors[1],
                mid: this.segmentColors[2],
                better: this.segmentColors[3],
                best: this.segmentColors[4],
            };
            if (this.vizOption == 'vizLastCheckin' || this.vizOption == 'vizDistance') {
                vizColors = {
                    worst: this.segmentColors[4],
                    bad: this.segmentColors[3],
                    mid: this.segmentColors[2],
                    better: this.segmentColors[1],
                    best: this.segmentColors[0],
                }                   
            }
            return vizColors;
        },

        filtersInPlace() {
            return !_.isEqual(this.moreParams, this.moreParamsDefault);
        },

        specialtiesChanged() {
            return (JSON.stringify(this.moreParams['filter[specialties]']) !== JSON.stringify(this.moreParamsDefault['filter[specialties]']));
        },
            
        lastCheckinChanged() {
            return (JSON.stringify(this.sliderRangeData.last_checkin) !== JSON.stringify(this.sliderRangeDataDefault.last_checkin));
        },

        referrerScoreChanged() {
            return (JSON.stringify(this.sliderRangeData.referrer_score) !== JSON.stringify(this.sliderRangeDataDefault.referrer_score));
        },

        referredPatientsChanged() {
            return (JSON.stringify(this.sliderRangeData.referred_patients) !== JSON.stringify(this.sliderRangeDataDefault.referred_patients));
        },

        patientVisitsChanged() {
            return (JSON.stringify(this.sliderRangeData.patient_visits) !== JSON.stringify(this.sliderRangeDataDefault.patient_visits));
        },

        generatedChargesChanged() {
            return (JSON.stringify(this.sliderRangeData.generated_charges) !== JSON.stringify(this.sliderRangeDataDefault.generated_charges));
        }, 

        distanceChanged() {
            return (JSON.stringify(this.sliderRangeData.distance) !== JSON.stringify(this.sliderRangeDataDefault.distance));
        },

        visualization_is_on() {
            return this.vizOption !== false;
        },

        selectedAllVisibleLocations() {
        	//select all checkbox will be set to checked on or off
        	return 
        		document.querySelectorAll('table.vuetable input[type="checkbox"]').length > 0 && 
        		document.querySelectorAll('table.vuetable input[type="checkbox"]').length === this.selectedItems.length;
        },

		selectedSomeVisibleLocations() {
			//select all checkbox will be set to the indeterminate state
			return (document.querySelectorAll('table.vuetable input[type="checkbox"]').length > this.selectedItems.length) && (this.selectedItems.length > 0);
		},        
    },

    watch: {
		'$root.$mq'() {
			this.setLayout();
		},

        mapExpanded() {
        	this.setLayout();
        },

        searchInMapWithinRadius() {
            Vue.nextTick( () => this.$refs.vuetable.refresh() );
        },

        searchInMapWithinRadiusOptions: {
            handler(newValue, oldValue){
                Vue.nextTick( () => this.$refs.vuetable.refresh() );
            }, 
            deep: true
        },

        showPrimaryLocationsOnly() {
        	//findIndex for the name isn't working -- hardcoded 0
        	this.showPrimaryLocationsOnly ? this.physicianFields[0].width = '242px' : this.physicianFields[0].width = '302px';
        	this.$refs.vuetable.normalizeFields();
        },

        sliderRangeData: {
            handler(newValue, oldValue){
                if (this.data_loaded && !this.resetTakingPlace) {
                    if (this.lastCheckinChanged) {
                        this.moreParams['filter[last_checkin]'] = this.sliderRangeData.last_checkin;
                    }
                    if (this.referrerScoreChanged) {
                        this.moreParams['filter[referrer_score]'] = this.sliderRangeData.referrer_score;
                    }
                    if (this.referredPatientsChanged) {
                        this.moreParams['filter[referred_patients]'] = this.sliderRangeData.referred_patients;
                    }
                    if (this.patientVisitsChanged) {
                        this.moreParams['filter[patient_visits]'] = this.sliderRangeData.patient_visits;
                    }
                    if (this.generatedChargesChanged) {
                        this.moreParams['filter[generated_charges]'] = this.sliderRangeData.generated_charges;
                    }
                    if (this.distanceChanged) {
                        this.moreParams['filter[distance]'] = this.sliderRangeData.distance;
                    }
                    Vue.nextTick( () => this.$refs.vuetable.refresh() );
                }
            },
            deep: true
        },

        selectedItems() {
        	if (this.save_physicians_first) {
        		this.physiciansToSaveFirst = this.physician_data.filter(radar_physician => this.selectedItems.some(item => item.morphable_id == radar_physician.id));
        	}
        },

        specialtyFilter() {
            if (this.data_loaded && !this.resetTakingPlace) {
                this.moreParams['filter[specialties]'] = this.specialtyFilter;
                Vue.nextTick( () => this.$refs.vuetable.refresh() );
            }
        },

        user_latitude() {
            if (this.physician_distance_use_clinic) {
                // console.log('using clinic... ');
                this.physician_distance_from_latitude = this.default_latitude;
                this.physician_distance_from_longitude = this.default_longitude;
            } else {
                // console.log('using user... ');
                this.physician_distance_from_latitude = this.user_latitude;
                this.physician_distance_from_longitude = this.user_longitude;
            }
        }
    },

  }
</script>

<style>
	.dropup .dropdown-toggle::after {
		display:none !important;
	}

	.vue-slider .vue-slider-dot-tooltip-inner { background-color:#ee2742; border-color:#ee2742; font-size:10px; font-weight:bold; padding:2px 2px 0; min-width:14px; line-height:13px;}
	.vue-slider .vue-slider-dot-tooltip-top { top:-6px; }
	.vue-slider .vue-slider-process { background-color:#999; }


	.vuetable-pagination-info {
		padding-left: 5px;
		min-width:120px;
	}

	.vuetable_fixed_footer.xs .vuetable-pagination-info {
		display:none;
	}
	.vuetable_fixed_footer.xs .pagination .first_page,
	.vuetable_fixed_footer.xs .pagination .last_page,
	.vuetable_fixed_footer.xs .pagination .specific_page_link {
		display:none;
	}

	.vuetable_fixed_footer.sm .pagination .first_page,
	.vuetable_fixed_footer.sm .pagination .last_page,
	.vuetable_fixed_footer.sm .pagination .specific_page_link {
		display:none;
	}

</style>